import React from 'react';
import ViewAgencyBalanceTransactionsButtonProperties from "./ViewAgencyBalanceTransactionsButtonProperties";
import {useI18nContext} from "../../../contexts/I18nContext";
import ViewAgencyBalanceTransactionsStore from "./ViewAgencyBalanceTransactionsStore";
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import RoundedButton from "../../../ui_components/RoundedButton/RoundedButton";
import {Observer} from "mobx-react-lite";
import {useStyles} from "./ViewAgencyBalanceTransactionsButton.style";
import Button from "../../../ui_components/Button/Button";
import ButtonType from "../../../ui_components/Button/ButtonType";
import {useCommonContext} from "../../../contexts/CommonContext";
import ButtonSize from "../../../ui_components/Button/ButtonSize";
import SidePanel from "../../../ui_components/SidePanel/SidePanel";
import {useUserContext} from "../../../contexts/UserContext";
import Access from "../../Access/AllowAccess/Access";
import ShowTransactionLegend from '../../Info/ShowTransactionLegend/ShowTransactionLegend';
import DateTimeRangeSelect from '../../../ui_components/DateTimeRageSelect/DateTimeRangeSelect';
import Box from '../../../ui_components/Box/Box';
import Alignment from '../../../ui_components/common/enums/Alignment';
import Width from '../../../ui_components/common/enums/Width';
import ShowPagination from '../../../ui_components/ShowPagination/ShowPagination';
import clsx from 'clsx';
import TransactionStatus from '../../../entities/TransactionStatus';
import ShowDate from '../../Info/ShowDate/ShowDate';
import ShowPrice from '../../Info/ShowPrice/ShowPrice';
import ShowGeneralComment from '../../Info/ShowGeneralComment/ShowGeneralComment';
import MessageCard from '../../../ui_components/MessageCard/MessageCard';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import AgencyBalanceTransaction from '../../../entities/AgencyBalanceTransaction';
import Currency from '../../../entities/Currency';
import AddAgencyBalanceButton from '../AddAgencyBalanceButton/AddAgencyBalanceButton';
import ShowAgencyBalance from '../../Info/ShowAgencyBalance/ShowAgencyBalance';
import ShowPaymentMethod from '../../Info/ShowPaymentMethod/ShowPaymentMethod';

const ViewAgencyBalanceTransactionsButton: React.FC<ViewAgencyBalanceTransactionsButtonProperties> = ({
                                                                                onClick = () => {
                                                                                },
                                                                                onSave = () => {
                                                                                },
                                                                                showIcon = true,
                                                                                showText = true,
                                                                                disabled,
                                                                                className = '',
                                                                                agencyId,
                                                                                rounded = false,
                                                                                size = ButtonSize.DEFAULT,
                                                                                type = ButtonType.DEFAULT
                                                                            }) => {
    const classes = useStyles(showText);

    const i18n = useI18nContext();
    const commonContext = useCommonContext();
    const userStore = useUserContext();
    const store = new ViewAgencyBalanceTransactionsStore(commonContext, agencyId);

    const handleOnclick = async () => {
        await store.openModal();
        onClick();
    }

    const onCloseModal = () => {
        store.closeModal();
        onSave && onSave();
    }

    const onUpdate = () => {
        store.loadAgencyBalance();
        store.goToPage(1, store.pagination.pageSize);
    }

    const text = i18n.translate("TRANSACTIONS")
    const Icon = AccountBalanceIcon;
    if (!userStore.hasAccess(Access.LIST_AGENCY_BALANCE_TRANSACTION.valueOf())) {
        return <></>;
    }
    const Btn = () => {
        if (rounded) {
            return <RoundedButton
                onClick={handleOnclick}
                className={className}
                disabled={disabled}
                tooltip={!showText ? text : ""}
                text={showText ? text : ""}
                type={type}
                icon={showIcon ? <Icon/> : <></>}
            />
        }
        return <Button
            text={showText ? text : ""}
            className={className}
            tooltip={!showText ? text : ""}
            disabled={disabled}
            size={size}
            onClick={handleOnclick}
            type={type}
            startIcon={showIcon ? <Icon/> : <></>}
        />
    }

    const onPaginationChange = async (page: number, pageSize: number) => {
        await store.goToPage(page, pageSize);
    }

    if (!userStore.hasAccess(Access.GET_TRANSACTIONS.valueOf())) {
        return <></>;
    }

    const getLeftBorderClass = (transaction: AgencyBalanceTransaction) => {
        if (transaction.amount <= 0) {
            return classes.out_border;
        }
        if (transaction.amount > 0) {
            return classes.in_border;
        }
        return undefined;
    }

    return <Observer>
        {() => (
            <>
                <SidePanel loading={store.loading} title={text} onClose={onCloseModal} open={store.isModalOpen}
                           className={classes.modal_container}>
                    <ShowTransactionLegend className={classes.legend_container} showTransferLegend={false}/>
                    
                    <div className={classes.contacts_container}>
                        <div className={classes.body}>
                            <Box className={classes.filter_container} width={Width.FULL_WIDTH}
                                    justifyContent={Alignment.SPACE_BETWEEN}>
                                <DateTimeRangeSelect
                                    selectedFrom={store.from}
                                    selectedTo={store.to}
                                    label={i18n.translate("DATE_RANGE")}
                                    onChange={(from, to) => {
                                        store.from = from;
                                        store.to = to;
                                    }}/>
                                <div></div>
                                <div></div>
                                <div></div>
                                <div></div>
                                <div className={classes.btn_container}>
                                    <Button
                                        onClick={() => {
                                            store.searchBalanceTransations()
                                        }}
                                        className={classes.search_btn}
                                        text={i18n.translate("SEARCH")}
                                    />
                                    <Button
                                        onClick={() => {
                                            store.cleanFilter()
                                        }}
                                        className={classes.search_btn}
                                        startIcon={<FilterAltOffIcon/>}
                                        tooltip={i18n.translate("CLEAN_FILTER")}
                                    />
                                </div>
                            </Box>
                            <Box className={classes.options_container}>
                                <ShowAgencyBalance value={store.agencyBalance ?? 0}/>
                                <AddAgencyBalanceButton agencyId={agencyId} onSave={onUpdate}/>
                            </Box>
                            {!store.isEmpty &&
                            <div className={classes.contacts_table}>
                                <ShowPagination onChange={onPaginationChange}
                                                elementLabel={i18n.translate("TRANSACTIONS")}
                                                pagination={store.pagination}/>
                                <div className={classes.elements_container}>
                                    {store.transactions.map((transaction: AgencyBalanceTransaction, i: React.Key | null | undefined) => (
                                        <div key={i} className={clsx(
                                            classes.contact_info_container,
                                            transaction.status === TransactionStatus.FAILURE && classes.error_container,
                                            getLeftBorderClass(transaction)
                                        )}>
                                            <ShowDate timestamp={transaction.createdAt}/>
                                            <ShowPrice
                                                value={transaction.amount}
                                                currency={Currency.USD}
                                                valueBefore={transaction.remainingBalanceAfter}
                                            />
                                            <ShowPaymentMethod paymentMethod={transaction.paymentMethod}/>
                                            <ShowGeneralComment value={transaction.comment}/>
                                        </div>
                                    ))}
                                </div>
                                <ShowPagination
                                    onChange={onPaginationChange}
                                    elementLabel={i18n.translate("TRANSACTIONS")}
                                    pagination={store.pagination}/>
                            </div>
                            }
                            {store.isEmpty &&
                            <MessageCard icon={AccountBalanceIcon} title={i18n.translate("NO_TRANSACTIONS_TITLE")}
                                subtitle={i18n.translate("NO_TRANSACTIONS_SUBTITLE")} className={classes.empty_container}/>}
                        </div>
                    </div>

                </SidePanel>
                <Btn/>
            </>
        )}
    </Observer>
}

export default ViewAgencyBalanceTransactionsButton;
